<template>
  <div class="app-container">
    <!-- 側邊導航欄 - 已由 App.vue 處理，此處移除 -->
    <!-- <admin-sidebar /> -->

    <!-- 主內容區域 - 直接顯示 -->
    <div class="category-manager">
      <div class="page-header">
        <div class="operations">
          <!-- <button @click="openAddCategoryDialog" class="add-btn">新增分類</button> -->
          <!-- <button @click="fetchCategories" class="refresh-btn">刷新資料</button> -->
        </div>
      </div>
      
      <div class="category-container">
        <!-- 分類樹 -->
        <div class="category-tree">
          
          <div v-if="isLoading && !treeCategories.length" class="loading">載入中...</div> 
          <div v-else-if="!isLoading && treeCategories.length === 0" class="empty-data">無分類資料</div>
          <category-tree
            v-else
            :data="treeCategories"
            :flat-categories="flatCategories"
            @request-edit="openEditCategoryDialog"
            @delete="confirmDelete"
            @update-tree="handleTreeUpdate"
            @request-add-child="handleAddChildRequest"
            @request-add="openAddCategoryDialog"
          />
        </div>
        
        <!-- 分類表單 -->
        <div v-if="isFormVisible" class="category-form">
          <!-- 標題和批次模式開關 -->
          <div class="form-header">
            <h2>{{ isEditMode ? '編輯分類' : '新增分類' }}</h2>
            <div class="batch-mode-toggle" v-if="!isEditMode">
              <label>
                <input type="checkbox" v-model="isBatchMode">
                啟用批次新增
              </label>
            </div>
          </div>

          <form @submit.prevent="persistCategory">
            <!-- 分類名稱 / 批次輸入 -->
            <div class="form-group">
              <label for="name">{{ isBatchMode ? '子分類名稱 (每行一個)' : '分類名稱*' }}</label>
              <textarea 
                v-if="isBatchMode" 
                id="name-batch"
                v-model="categoryForm.name" 
                required 
                placeholder="例如：
子分類A
子分類B
子分類C"
                rows="5" 
                ref="nameInputRef"
              ></textarea>
              <input 
                v-else
                id="name"
                v-model="categoryForm.name" 
                type="text" 
                required
                placeholder="請輸入分類名稱"
                ref="nameInputRef"
              >
            </div>
            
            <!-- 分類描述 (批次模式下通常不需要) -->
            <div class="form-group" v-if="!isBatchMode">
              <label for="description">分類描述</label>
              <textarea 
                id="description" 
                v-model="categoryForm.description" 
                placeholder="請輸入分類描述"
              ></textarea>
            </div>
            
            <!-- 父分類 (批次模式下必選) -->
            <div class="form-group">
              <label for="parent">父分類 {{ isBatchMode ? '*' : '' }}</label>
              <select id="parent" v-model="categoryForm.parent_id" :required="isBatchMode">
                <option :value="null">無 (頂級分類)</option>
                <option 
                  v-for="category in parentOptions" 
                  :key="category.id" 
                  :value="category.id"
                  :disabled="category.id === categoryForm.id || (isBatchMode && category.id === null)"
                >
                  {{ category.name }}
                </option>
              </select>
              <small v-if="isBatchMode" class="form-hint">批次新增必須選擇一個父分類。</small>
            </div>
            
            <!-- 顯示順序 (批次模式下由後端處理) -->
            <div class="form-group" v-if="!isBatchMode">
              <label for="display_order">顯示順序</label>
              <input 
                id="display_order" 
                v-model.number="categoryForm.display_order" 
                type="number"
                min="0"
              >
            </div>
            
            <!-- 啟用狀態 (批次模式下默認為啟用) -->
            <div class="form-group" v-if="!isBatchMode">
              <label class="checkbox-label">
                <input type="checkbox" v-model="categoryForm.is_active">
                <span>啟用</span>
              </label>
            </div>
            
            <div class="form-actions">
              <button type="submit" class="save-btn" :disabled="isSaving">{{ isSaving ? '儲存中...' : (isBatchMode ? '批次儲存' : '儲存') }}</button>
              <button type="button" @click="cancelEdit" class="cancel-btn">取消</button>
            </div>
          </form>
        </div>
      </div>
      
      <div v-if="error" class="error-message">
        {{ error }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, provide, nextTick } from 'vue'
import axios from 'axios'
// import AdminSidebar from '../components/AdminSidebar.vue' // 移除引入
import CategoryTree from '../components/CategoryTree.vue'

// 表單數據
const defaultFormData = {
  id: null,
  name: '',
  description: '',
  parent_id: null,
  display_order: 0,
  is_active: true
}

// 狀態和函數
const isLoading = ref(true);
const error = ref('');
const categories = ref([]);
const treeCategories = ref([]);
const flatCategories = ref([]);
const categoryForm = ref({ ...defaultFormData });
const isEditMode = ref(false);
const isSaving = ref(false);
const nameInputRef = ref(null);
const isFormVisible = ref(false);
const showDeleteConfirmation = ref(false);
const categoryToDelete = ref(null);
const isBatchMode = ref(false);

// 父分類選項 (過濾掉自己和子分類)
const parentOptions = computed(() => {
  if (!isEditMode.value) {
    return [...flatCategories.value];
  }

  if (!categoryForm.value.id) {
    console.warn("編輯模式下缺少 categoryForm.id，無法計算父分類選項");
    return [...flatCategories.value];
  }

  const currentCategoryId = parseInt(String(categoryForm.value.id), 10);
  if (isNaN(currentCategoryId)) {
    console.warn("當前編輯的分類ID無效，無法計算父分類選項。");
    return [...flatCategories.value];
  }

  const excludeIds = new Set();

  function findAllDescendantIds(categoryId) {
    if (excludeIds.has(categoryId)) return;
    excludeIds.add(categoryId);
    
    const children = flatCategories.value.filter(c => c.parent_id === categoryId);
    children.forEach(child => {
      if (child.id !== null) {
        findAllDescendantIds(parseInt(String(child.id), 10));
      }
    });
  }

  findAllDescendantIds(currentCategoryId);

  return flatCategories.value.filter(c => c.id !== null && !excludeIds.has(parseInt(String(c.id), 10)));
});

// 載入分類列表
async function fetchCategories() {
  isLoading.value = true;
  error.value = '';
  try {
    const response = await axios.get('/admin/categories');
    const data = response.data;

    if (!data) {
      throw new Error('API響應為空');
    }

    // 處理可能的API響應格式
    if (data.flat && Array.isArray(data.flat)) {
      const flatData = data.flat;
      categories.value = flatData;
      flatCategories.value = flatData;
      treeCategories.value = buildTree(flatData);
    } else if (Array.isArray(data)) {
      console.log('使用數組格式的 API 響應');
      const flatData = data;
      categories.value = flatData;
      flatCategories.value = flatData;
      treeCategories.value = buildTree(flatData);
    } else {
      console.error('不支持的 API 響應格式:', typeof data);
      throw new Error('API響應格式不符合預期，請聯繫技術支持');
    }
    // 新增：打印最新的樹結構

  } catch (err) {
    console.error('加載分類失敗:', err);
    if (err.response) {
      error.value = `加載分類失敗: ${err.response.status} - ${err.response.data?.error || err.message || '未知錯誤'}`;
    } else if (err.request) {
      error.value = '未收到伺服器響應，請確認伺服器是否正常運行';
    } else {
      error.value = `加載分類失敗: ${err.message}`;
    }
    categories.value = []; 
    treeCategories.value = [];
    flatCategories.value = [];
  } finally {
    isLoading.value = false;
  }
}

// 構建樹形結構數據
function buildTree(flatData) {
  // 建立 id 到節點的映射
  const map = {};
  flatData.forEach(item => {
    map[item.id] = { ...item, children: [] };
  });
  
  // 構建樹
  const tree = [];
  flatData.forEach(item => {
    const node = map[item.id];
    if (item.parent_id) {
      // 有父節點，添加到父節點的 children
      if (map[item.parent_id]) {
        map[item.parent_id].children.push(node);
        // 按 display_order 排序子節點
        map[item.parent_id].children.sort((a, b) => a.display_order - b.display_order);
      } else {
        console.warn(`找不到 ID 為 ${item.parent_id} 的父節點，將 ${item.id} 作為頂級節點處理`);
        tree.push(node);
      }
    } else {
      // 無父節點，作為頂級節點
      tree.push(node);
    }
  });
  
  // 按 display_order 排序頂級節點
  tree.sort((a, b) => a.display_order - b.display_order);
  
  return tree;
}

// 打開和關閉彈窗的函數
function openAddCategoryDialog() {
  isEditMode.value = false;
  isBatchMode.value = false;
  categoryForm.value = { ...defaultFormData };
  isSaving.value = false;
  isFormVisible.value = true;
  nextTick(() => {
    nameInputRef.value?.focus();
  });
}

function openEditCategoryDialog(category) {
  isEditMode.value = true;
  isBatchMode.value = false;
  categoryForm.value = { ...category, is_active: Boolean(category.is_active) };
  isSaving.value = false;
  isFormVisible.value = true;
  nextTick(() => {
    nameInputRef.value?.focus();
  });
}

// 取消編輯
function cancelEdit() {
  categoryForm.value = { ...defaultFormData };
  isEditMode.value = false;
  error.value = '';
  isSaving.value = false;
  isFormVisible.value = false;
}

// 確認刪除
function confirmDelete(category) {
  if (!confirm(`確定要刪除「${category.name}」分類嗎？這個操作不可恢復！`)) {
    return;
  }
  deleteCategory(category.id);
}

// 刪除分類
async function deleteCategory(categoryId) {
  isLoading.value = true;
  error.value = '';
  try {
    console.log(`準備刪除分類 ID: ${categoryId}`);
    await axios.delete(`/admin/categories/${categoryId}`);
    console.log('刪除成功，重新加載數據...');
    await fetchCategories();
  } catch (err) {
    console.error('刪除分類失敗:', err);
    if (err.response) {
      // 檢查錯誤信息
      if (err.response.status === 400) {
        let errorDetails = '';
        if (err.response.data) {
          if (typeof err.response.data === 'string') {
            errorDetails = err.response.data;
          } else if (err.response.data.error || err.response.data.message) {
            errorDetails = err.response.data.error || err.response.data.message;
          }
        }
        
        // 根據錯誤信息提供更具體的提示
        if (errorDetails.includes('child')) {
          error.value = `無法刪除：該分類下仍有子分類，請先刪除所有子分類。`;
        } else if (errorDetails.includes('product') || errorDetails.includes('商品')) {
          error.value = `無法刪除：該分類下仍有關聯商品，請先移除所有關聯商品。`;
        } else {
          error.value = `刪除失敗：該分類可能仍包含子分類或關聯商品，請先處理相關數據後再嘗試刪除。(${errorDetails || '未知錯誤'})`;
        }
        
        // 檢查子分類
        const childCategories = categories.value.flat.filter(c => c.parent_id === categoryId || parseInt(c.parent_id) === parseInt(categoryId));
        if (childCategories.length > 0) {
          console.log(`發現 ${childCategories.length} 個子分類:`, childCategories.map(c => `${c.id}: ${c.name}`).join(', '));
          error.value += ` (發現 ${childCategories.length} 個子分類)`;
        }
      } else {
        error.value = `刪除分類失敗 (錯誤碼 ${err.response.status})：${err.response.data?.error || err.response.data?.message || err.message || '未知伺服器錯誤'}`;
      }
    } else if (err.request) {
      error.value = '刪除請求發送失敗：無法連接到伺服器或伺服器無響應。請檢查網絡連接和後端服務狀態。'
    } else {
      error.value = `刪除操作出錯：${err.message}`;
    }
  } finally {
    isLoading.value = false;
  }
}

// 保存分類
async function persistCategory() {
  isSaving.value = true;
  error.value = '';

  if (isBatchMode.value) {
    // --- 批次新增邏輯 ---
    const parentId = categoryForm.value.parent_id;
    if (parentId === null || parentId === undefined) {
      alert('批次新增必須選擇一個父分類！');
      error.value = '請選擇父分類。';
      isSaving.value = false;
      return;
    }

    const names = categoryForm.value.name
      .split('\n') // 按換行符分割
      .map(name => name.trim()) // 去除前後空白
      .filter(name => name !== ''); // 過濾空行

    if (names.length === 0) {
      alert('請至少輸入一個子分類名稱！');
      error.value = '子分類名稱不能為空。';
      isSaving.value = false;
      return;
    }

    console.log(`準備批次新增 ${names.length} 個子分類到父分類 ${parentId} 下:`, names);

    const results = { success: [], failed: [] };
    
    // 串行執行 (一個接一個)，方便追蹤錯誤
    for (const name of names) {
        const payload = {
            name: name,
            parent_id: parentId,
            is_active: true, // 批次新增默認啟用
            // display_order 讓後端處理
        };
        try {
            console.log('發送 POST 請求 (批次):', payload);
            await axios.post('/admin/categories', payload);
            results.success.push(name);
        } catch (err) {
            console.error(`批次新增失敗 (${name}):`, err);
            const errMsg = err.response?.data?.message || err.response?.data?.error || err.message || '未知錯誤';
            results.failed.push({ name, error: errMsg });
            // 可以選擇在這裡停止，或繼續嘗試其他的
            // break; // 如果希望一遇到錯誤就停止
        }
    }

    // 處理結果
    let message = '';
    if (results.success.length > 0) {
        message += `成功新增 ${results.success.length} 個分類: ${results.success.join(', ')}\n`;
    }
    if (results.failed.length > 0) {
        message += `新增失敗 ${results.failed.length} 個分類:\n`;
        results.failed.forEach(f => {
            message += `- ${f.name}: ${f.error}\n`;
        });
        error.value = `部分分類新增失敗，請檢查錯誤訊息。`; // 在表單下方顯示提示
    }

    alert(message); // 彈窗顯示詳細結果

    if (results.success.length > 0 || results.failed.length === 0) {
        // 只要有成功或全部成功，就關閉表單並刷新
        cancelEdit();
        await fetchCategories();
    } 
    // 如果全部失敗，保留表單讓用戶修改

    isSaving.value = false;

  } else {
    // --- 原本的單個新增/編輯邏輯 ---
    try {
      const dataToSave = {
        name: categoryForm.value.name,
        description: categoryForm.value.description,
        parent_id: categoryForm.value.parent_id,
        display_order: categoryForm.value.display_order,
        is_active: categoryForm.value.is_active
      };

      let response;
      if (isEditMode.value && categoryForm.value.id) {
        // 更新
        console.log(`發送 PUT 請求到 /admin/categories/${categoryForm.value.id}`, dataToSave);
        response = await axios.put(`/admin/categories/${categoryForm.value.id}`, dataToSave);
        alert('分類更新成功！');
      } else {
        // 新增
        console.log('發送 POST 請求到 /admin/categories', dataToSave);
        response = await axios.post('/admin/categories', dataToSave);
        alert('分類新增成功！');
      }
      console.log('後端回應:', response.data);
      cancelEdit(); 
      await fetchCategories(); 

    } catch (err) {
      console.error('儲存分類失敗:', err);
      if (err.response && err.response.data) {
         if (err.response.data.errors) {
             const errors = err.response.data.errors;
             const firstErrorKey = Object.keys(errors)[0];
             error.value = `儲存失敗：${errors[firstErrorKey][0]}`;
         } else {
             error.value = `儲存失敗：${err.response.data.message || err.response.data.error || '伺服器錯誤'}`;
         }
      } else {
          error.value = `儲存分類失敗: ${err.message || '未知錯誤'}`;
      }
    } finally {
      isSaving.value = false;
    }
  }
}

// --- 新增：扁平化樹結構 --- 
function flattenTree(nodes, parentId = null, orderMap = {}) {
  let flatList = [];
  nodes.forEach((node, index) => {
    const newNode = { 
      ...node, // 保留原始信息
      parent_id: parentId, // 更新 parent_id
      display_order: index, // 更新 display_order
      children: undefined // 扁平化後移除 children 引用
    };
    flatList.push(newNode);
    // 記錄順序，用於快速查找
    if(!orderMap[parentId || 'root']) orderMap[parentId || 'root'] = {};
    orderMap[parentId || 'root'][node.id] = index;

    if (node.children && node.children.length > 0) {
      const { flatList: childFlatList } = flattenTree(node.children, node.id, orderMap);
      flatList = flatList.concat(childFlatList);
    }
  });
  return { flatList, orderMap };
}

// --- 修改：處理樹更新事件的核心方法 --- 
async function handleTreeUpdate(updatedTreeData) {
  console.log('[CategoryView] Received update-tree event with new tree data:', 
    JSON.parse(JSON.stringify(updatedTreeData)) // 打印乾淨數據
  );

  // 避免處理中重複觸發
  if (isLoading.value) {
    console.log('[CategoryView] 更新處理中，忽略此次請求');
    return;
  }

  try {
    // 1. 扁平化更新後的樹結構，並獲取新的 parent_id 和 display_order
    const { flatList: newFlatList } = flattenTree(updatedTreeData);
    console.log('[CategoryView] Flattened new tree:', newFlatList.map(c => ({id: c.id, name: c.name, parent: c.parent_id, order: c.display_order})));

    // 2. 與原始扁平列表 (categories.value) 比較，找出差異
    const updates = [];
    const originalMap = new Map(categories.value.map(c => [String(c.id), c]));

    newFlatList.forEach(newNode => {
      const originalNode = originalMap.get(String(newNode.id));
      if (originalNode) {
        const parentChanged = String(originalNode.parent_id || '') !== String(newNode.parent_id || '');
        const orderChanged = originalNode.display_order !== newNode.display_order;

        if (parentChanged || orderChanged) {
          updates.push({
            id: newNode.id,
            parent_id: newNode.parent_id, // 使用新的 parent_id
            display_order: newNode.display_order // 使用新的 display_order
          });
        }
      } else {
        // 理論上不應該發生，表示新列表裡有原始列表沒有的節點
        console.warn('[CategoryView] Node from new tree not found in original list:', newNode);
      }
    });

    // 3. 如果有差異，發送請求
    if (updates.length > 0) {
      console.log(`[CategoryView] Calculated updates (${updates.length}個):`, updates);
      
      isLoading.value = true;
      try {
        // 發送批量更新請求
        const response = await axios.put('/admin/categories/batch/order', { updates });
       
        if (response.status === 200) {
          console.log('分類順序更新成功，重新加載數據...');
          // 延遲一下再重新加載，讓後端有時間處理
          setTimeout(() => fetchCategories(), 300);
        }
      } catch (err) {
        handleApiError(err, '更新分類順序失敗');
        // 如果更新失敗，回滾到原始數據
        console.log('更新失敗，重新加載原始數據');
        await fetchCategories();
      } finally {
        isLoading.value = false;
      }
    } else {
      console.log('[CategoryView] No changes detected, no update needed.');
    }
  } catch (err) {
    console.error('處理樹更新時發生錯誤:', err);
    error.value = `處理更新時發生錯誤: ${err.message || '未知錯誤'}`;
    isLoading.value = false;
  }
}

// 新增：API錯誤處理函數
function handleApiError(err, prefix = '操作失敗') {
  console.error(prefix + ':', err);
  
  if (err.code === 'ERR_NETWORK') {
    error.value = `${prefix}: 無法連接到伺服器，請檢查網絡連接或伺服器狀態`;
    alert('無法連接到伺服器，請重新整理頁面後再試。');
  } else if (err.response) {
    error.value = `${prefix}: ${err.response.data?.error || err.response.data?.message || err.message || '未知錯誤'}`;
  } else {
    error.value = `${prefix}: ${err.message || '未知錯誤'}`;
  }
}

// 處理來自 CategoryTree 的新增子分類請求
const handleAddChildRequest = (parentNode) => {
  console.log(`[CategoryView] Handling add child request for parent: ${parentNode.name}`);
  openAddCategoryDialog(); 
  categoryForm.value.parent_id = parentNode.id; 
  // 點擊 '+' 按鈕時，預設關閉批次模式
  isBatchMode.value = false; 
};

// 生命週期鉤子
onMounted(() => {
  fetchCategories();
});
</script>

<style scoped>
.app-container {
  display: flex;
  min-height: 100vh;
}
.category-manager {
  width: 100%;          /* 確保寬度為100% */
  box-sizing: border-box; /* 確保 padding 不影響寬度 */
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}



.operations {
  display: flex;
  gap: 10px;
}

button {
  padding: 8px 16px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  transition: all 0.3s;
}

.add-btn {
  background-color: #409EFF;
  color: white;
}

.add-btn:hover {
  background-color: #66b1ff;
}

.refresh-btn {
  background-color: #67C23A;
  color: white;
}

.refresh-btn:hover {
  background-color: #85ce61;
}

.category-container {
  display: grid;
  gap: 20px;
  margin-bottom: 20px;
}

@media (max-width: 768px) {
  .category-container {
    grid-template-columns: 1fr;
  }
}

.category-tree,
.category-form {
  background-color: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
  /* 新增：限制最大高度並啟用滾動 */
  max-height: calc(100vh - 200px);
  overflow-y: auto;
  position: relative;
}

.category-tree {
  border: 1px solid #ebeef5;
  background-color: #fff;
}

.category-tree h2,
.category-form h2 {
  font-size: 18px;
  margin-top: 0;
  margin-bottom: 20px;
  color: #303133;
  font-weight: 600;
  border-bottom: 1px solid #ebeef5;
  padding-bottom: 12px;
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;
}

/* 優化滾動條樣式 */
.category-tree,
.category-form {
  scrollbar-width: thin;
  scrollbar-color: #909399 #f4f4f5;
}

.category-tree::-webkit-scrollbar,
.category-form::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.category-tree::-webkit-scrollbar-track,
.category-form::-webkit-scrollbar-track {
  background: #f4f4f5;
  border-radius: 3px;
}

.category-tree::-webkit-scrollbar-thumb,
.category-form::-webkit-scrollbar-thumb {
  background: #909399;
  border-radius: 3px;
}

.category-tree::-webkit-scrollbar-thumb:hover,
.category-form::-webkit-scrollbar-thumb:hover {
  background: #606266;
}

.loading,
.empty-data {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #909399;
}

.form-group {
  margin-bottom: 15px;
}

label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  color: #606266;
}

input[type="text"],
input[type="number"],
textarea,
select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 14px;
  color: #606266;
  transition: border-color 0.3s;
}

input[type="text"]:focus,
input[type="number"]:focus,
textarea:focus,
select:focus {
  outline: none;
  border-color: #409EFF;
}

textarea {
  height: 100px;
  resize: vertical;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: normal;
}

input[type="checkbox"] {
  width: auto;
  margin: 0;
}

.form-actions {
  display: flex;
  gap: 10px;
  margin-top: 20px;
}

.save-btn {
  background-color: #409EFF;
  color: white;
}

.save-btn:hover {
  background-color: #66b1ff;
}

.cancel-btn {
  background-color: #909399;
  color: white;
}

.cancel-btn:hover {
  background-color: #a6a9ad;
}

.error-message {
  padding: 10px 15px;
  background-color: #fef0f0;
  color: #f56c6c;
  border-radius: 4px;
  margin-top: 20px;
  font-size: 14px;
  line-height: 1.5;
}

/* 新增樣式 */
.form-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.batch-mode-toggle label {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-weight: normal;
  color: #303133;
}

.batch-mode-toggle input[type="checkbox"] {
  margin-right: 5px;
}

.form-hint {
  display: block;
  font-size: 0.85em;
  color: #909399;
  margin-top: 4px;
}

/* 調整文字區域樣式 */
textarea[id="name-batch"] {
  min-height: 100px; /* 給多點空間 */
  resize: vertical;
}
</style>
